// Business Applications and Offerings
// Maps Peak Priorities system to concrete revenue-generating activities

export interface BusinessOffering {
  id: string;
  name: string;
  type: 'workshop' | 'consulting' | 'artifact' | 'speaking' | 'thought-leadership';
  description: string;
  targetAudience: string[];
  duration?: string;
  deliverables: string[];
  sourceModels: string[]; // Which M2 models this offering is based on
  revenuePotential: 'high' | 'medium' | 'low';
  developmentStatus: 'ready' | 'in-development' | 'planned';
  keyValueProps: string[];
  pricingModel?: string;
  prerequisites?: string[];
}

export interface BusinessApplication {
  id: string;
  name: string;
  description: string;
  offerings: BusinessOffering[];
  totalRevenuePotential: 'high' | 'medium' | 'low';
  marketSize: string;
  competitiveAdvantage: string[];
}

export const businessApplications: BusinessApplication[] = [
  {
    id: 'workshops',
    name: 'Workshops & Training Programs',
    description: 'Interactive learning experiences that teach prioritization frameworks and capabilities',
    totalRevenuePotential: 'high',
    marketSize: 'Corporate training market ($366B globally)',
    competitiveAdvantage: [
      'Proven frameworks with measurable outcomes',
      'Interactive, hands-on learning approach',
      'Customizable for different organizational contexts',
      'Built-in assessment and progress tracking'
    ],
    offerings: [
      {
        id: 'degap-workshop',
        name: 'DEGAP Prioritization Workshop',
        type: 'workshop',
        description: '2-day intensive workshop teaching the 5-stage DEGAP framework for effective prioritization',
        targetAudience: ['Product managers', 'Team leads', 'Executives', 'Project managers'],
        duration: '2 days',
        deliverables: [
          'Interactive workshop materials',
          'Personal prioritization assessment',
          'Team prioritization framework',
          '30-day implementation plan',
          'Follow-up coaching session'
        ],
        sourceModels: ['pf.degap.stages', 'pf.capability.model'],
        revenuePotential: 'high',
        developmentStatus: 'ready',
        keyValueProps: [
          'Immediate practical application',
          'Measurable skill improvement',
          'Team alignment on priorities',
          'Reduced decision fatigue'
        ],
        pricingModel: '$15,000-25,000 per workshop',
        prerequisites: ['Minimum 8 participants', 'Pre-workshop assessment']
      },
      {
        id: 'capability-assessment-workshop',
        name: '12 Capabilities Assessment Workshop',
        type: 'workshop',
        description: 'Half-day workshop focused on assessing and developing the 12 core prioritization capabilities',
        targetAudience: ['HR professionals', 'Learning & development teams', 'Individual contributors'],
        duration: '4 hours',
        deliverables: [
          'Individual capability assessment',
          'Team capability mapping',
          'Development roadmap',
          'Capability tracking tools'
        ],
        sourceModels: ['pf.capability.model'],
        revenuePotential: 'medium',
        developmentStatus: 'ready',
        keyValueProps: [
          'Clear skill gap identification',
          'Personalized development plans',
          'Team capability optimization'
        ],
        pricingModel: '$5,000-8,000 per workshop'
      },
      {
        id: 'awareness-frameworks-workshop',
        name: 'Advanced Awareness Frameworks Workshop',
        type: 'workshop',
        description: 'Deep-dive into Johari Window, Logical Levels, and Ladder of Inference for advanced practitioners',
        targetAudience: ['Coaches', 'Consultants', 'Senior leaders', 'Change management professionals'],
        duration: '1 day',
        deliverables: [
          'Framework mastery certification',
          'Facilitation toolkit',
          'Client application templates',
          'Advanced assessment tools'
        ],
        sourceModels: ['pf.awareness.johari', 'pf.awareness.logical', 'pf.awareness.ladder'],
        revenuePotential: 'high',
        developmentStatus: 'in-development',
        keyValueProps: [
          'Advanced practitioner certification',
          'Client facilitation capabilities',
          'Premium positioning'
        ],
        pricingModel: '$8,000-12,000 per workshop'
      }
    ]
  },
  {
    id: 'consulting',
    name: 'Consulting & Advisory Services',
    description: 'Strategic consulting engagements using Peak Priorities frameworks to solve organizational challenges',
    totalRevenuePotential: 'high',
    marketSize: 'Management consulting market ($260B globally)',
    competitiveAdvantage: [
      'Proven methodology with measurable results',
      'Deep expertise in prioritization challenges',
      'Customizable frameworks for any industry',
      'Data-driven approach to decision making'
    ],
    offerings: [
      {
        id: 'strategic-prioritization-consulting',
        name: 'Strategic Prioritization Consulting',
        type: 'consulting',
        description: '3-6 month engagement to transform organizational prioritization capabilities',
        targetAudience: ['C-suite executives', 'Strategy teams', 'Operations leaders'],
        deliverables: [
          'Current state assessment',
          'Prioritization framework design',
          'Implementation roadmap',
          'Training program development',
          'Success metrics and tracking',
          'Quarterly review sessions'
        ],
        sourceModels: ['mof.strategy_delivery_cascade_by_scope', 'pf.degap.stages', 'pf.capability.model'],
        revenuePotential: 'high',
        developmentStatus: 'ready',
        keyValueProps: [
          'Transformed decision-making culture',
          'Improved strategic alignment',
          'Reduced resource waste',
          'Faster time to market'
        ],
        pricingModel: '$150,000-300,000 per engagement'
      },
      {
        id: 'outcome-driver-consulting',
        name: 'Outcome Driver Framework Consulting',
        type: 'consulting',
        description: 'Focused engagement to implement outcome-driven thinking across the organization',
        targetAudience: ['Product teams', 'Marketing teams', 'Sales teams'],
        duration: '2-3 months',
        deliverables: [
          'Outcome driver templates',
          'Team training sessions',
          'Process integration guide',
          'Success measurement framework'
        ],
        sourceModels: ['gf.outcome.driver', 'gf.desired.outcome'],
        revenuePotential: 'medium',
        developmentStatus: 'ready',
        keyValueProps: [
          'Clearer value proposition development',
          'Better customer outcome focus',
          'Improved project success rates'
        ],
        pricingModel: '$75,000-125,000 per engagement'
      }
    ]
  },
  {
    id: 'artifacts',
    name: 'Long-Form Content & Artifacts',
    description: 'Comprehensive content pieces that establish thought leadership and generate leads',
    totalRevenuePotential: 'medium',
    marketSize: 'Content marketing market ($42B globally)',
    competitiveAdvantage: [
      'Deep, research-backed content',
      'Practical frameworks readers can implement',
      'Unique insights from extensive experience',
      'Comprehensive coverage of prioritization topics'
    ],
    offerings: [
      {
        id: 'prioritization-playbook',
        name: 'The Peak Priorities Playbook',
        type: 'artifact',
        description: 'Comprehensive guide to prioritization frameworks and implementation strategies',
        targetAudience: ['Business leaders', 'Product managers', 'Consultants', 'Coaches'],
        deliverables: [
          '300+ page comprehensive guide',
          'Implementation templates',
          'Assessment tools',
          'Case studies and examples',
          'Digital companion resources'
        ],
        sourceModels: ['pf.degap.stages', 'pf.capability.model', 'mof.strategy_delivery_cascade_by_scope'],
        revenuePotential: 'medium',
        developmentStatus: 'in-development',
        keyValueProps: [
          'Comprehensive reference guide',
          'Immediate practical application',
          'Thought leadership positioning',
          'Lead generation tool'
        ],
        pricingModel: '$97-197 per copy'
      },
      {
        id: 'awareness-frameworks-guide',
        name: 'Advanced Awareness Frameworks Guide',
        type: 'artifact',
        description: 'Deep-dive guide to Johari Window, Logical Levels, and Ladder of Inference for practitioners',
        targetAudience: ['Coaches', 'Consultants', 'HR professionals', 'Therapists'],
        deliverables: [
          '200+ page specialized guide',
          'Facilitation templates',
          'Assessment instruments',
          'Client application examples'
        ],
        sourceModels: ['pf.awareness.johari', 'pf.awareness.logical', 'pf.awareness.ladder'],
        revenuePotential: 'medium',
        developmentStatus: 'planned',
        keyValueProps: [
          'Specialized practitioner resource',
          'Premium positioning',
          'Professional development tool'
        ],
        pricingModel: '$147-297 per copy'
      }
    ]
  },
  {
    id: 'speaking',
    name: 'Speaking Engagements & Keynotes',
    description: 'High-impact presentations that showcase Peak Priorities frameworks and generate business opportunities',
    totalRevenuePotential: 'high',
    marketSize: 'Professional speaking market ($2.3B globally)',
    competitiveAdvantage: [
      'Compelling frameworks with immediate applicability',
      'Engaging presentation style',
      'Measurable audience impact',
      'Strong follow-up conversion rates'
    ],
    offerings: [
      {
        id: 'prioritization-keynote',
        name: 'The Science of Smart Prioritization',
        type: 'speaking',
        description: '60-90 minute keynote on the frameworks and science behind effective prioritization',
        targetAudience: ['Conference attendees', 'Corporate audiences', 'Professional associations'],
        duration: '60-90 minutes',
        deliverables: [
          'Dynamic keynote presentation',
          'Audience engagement activities',
          'Takeaway resources',
          'Follow-up materials'
        ],
        sourceModels: ['pf.degap.stages', 'pf.capability.model'],
        revenuePotential: 'high',
        developmentStatus: 'ready',
        keyValueProps: [
          'Immediate audience engagement',
          'Practical frameworks to implement',
          'Strong brand visibility',
          'High conversion to consulting'
        ],
        pricingModel: '$15,000-25,000 per keynote'
      },
      {
        id: 'outcome-driver-presentation',
        name: 'Outcome-Driven Thinking: Beyond Features to Value',
        type: 'speaking',
        description: '45-60 minute presentation on outcome-driven product and service development',
        targetAudience: ['Product managers', 'Marketing teams', 'Startup founders'],
        duration: '45-60 minutes',
        deliverables: [
          'Focused presentation',
          'Interactive exercises',
          'Implementation templates',
          'Resource list'
        ],
        sourceModels: ['gf.outcome.driver', 'gf.desired.outcome'],
        revenuePotential: 'medium',
        developmentStatus: 'ready',
        keyValueProps: [
          'Clear value proposition focus',
          'Immediate application potential',
          'Audience-specific content'
        ],
        pricingModel: '$8,000-15,000 per presentation'
      }
    ]
  },
  {
    id: 'thought-leadership',
    name: 'Thought Leadership & Content Strategy',
    description: 'Strategic content that positions Peak Priorities as the leading authority on prioritization',
    totalRevenuePotential: 'medium',
    marketSize: 'Thought leadership drives 40% of B2B sales',
    competitiveAdvantage: [
      'Unique frameworks not available elsewhere',
      'Research-backed insights',
      'Practical implementation focus',
      'Strong personal brand'
    ],
    offerings: [
      {
        id: 'prioritization-research',
        name: 'Annual Prioritization Research Report',
        type: 'thought-leadership',
        description: 'Comprehensive research on prioritization challenges and solutions across industries',
        targetAudience: ['Business leaders', 'Researchers', 'Media', 'Consultants'],
        deliverables: [
          '50+ page research report',
          'Executive summary',
          'Industry-specific insights',
          'Implementation recommendations',
          'Media kit and press release'
        ],
        sourceModels: ['pf.degap.stages', 'pf.capability.model', 'mof.strategy_delivery_cascade_by_scope'],
        revenuePotential: 'medium',
        developmentStatus: 'planned',
        keyValueProps: [
          'Industry authority positioning',
          'Media coverage opportunities',
          'Lead generation tool',
          'Speaking engagement driver'
        ],
        pricingModel: 'Free with lead capture'
      },
      {
        id: 'framework-innovation',
        name: 'Framework Innovation Series',
        type: 'thought-leadership',
        description: 'Regular content series introducing new frameworks and refinements to existing ones',
        targetAudience: ['Practitioners', 'Consultants', 'Coaches', 'Researchers'],
        deliverables: [
          'Monthly framework releases',
          'Implementation guides',
          'Case studies',
          'Community discussions'
        ],
        sourceModels: ['All M2 models'],
        revenuePotential: 'low',
        developmentStatus: 'in-development',
        keyValueProps: [
          'Continuous innovation positioning',
          'Community engagement',
          'Early adopter benefits',
          'Feedback loop for improvement'
        ],
        pricingModel: 'Free with community engagement'
      }
    ]
  }
];

// Helper functions
export const getOfferingsByType = (type: BusinessOffering['type']): BusinessOffering[] => {
  return businessApplications.flatMap(app => 
    app.offerings.filter(offering => offering.type === type)
  );
};

export const getOfferingsByRevenue = (potential: BusinessOffering['revenuePotential']): BusinessOffering[] => {
  return businessApplications.flatMap(app => 
    app.offerings.filter(offering => offering.revenuePotential === potential)
  );
};

export const getOfferingsByStatus = (status: BusinessOffering['developmentStatus']): BusinessOffering[] => {
  return businessApplications.flatMap(app => 
    app.offerings.filter(offering => offering.developmentStatus === status)
  );
};

export const getTotalRevenuePotential = (): { [key: string]: number } => {
  const totals = { high: 0, medium: 0, low: 0 };
  businessApplications.forEach(app => {
    app.offerings.forEach(offering => {
      totals[offering.revenuePotential]++;
    });
  });
  return totals;
};
